/**
 * Running a council means waiting on several models plus a judge. The blocking
 * route answers once, at the end, so the user watches nothing happen for as
 * long as the slowest member takes. The server already reports each member as
 * it lands on POST /api/council/stream; this is the client half of that.
 *
 * The blocking route stays as the fallback. A stream that never starts is a
 * transport problem, not a council problem, and must not cost the user their
 * message.
 */

/**
 * Server-sent events arrive in whatever pieces the network hands over. A line
 * can be cut in half between two chunks, so lines are only parsed once their
 * newline has arrived.
 */
export function createSseParser(onEvent) {
  let buffer = "";

  const take = (line) => {
    if (!line.startsWith("data: ")) return;
    const payload = line.slice(6).trim();
    if (!payload) return;
    onEvent(JSON.parse(payload));
  };

  return {
    push(text) {
      buffer += text;
      let cut;
      while ((cut = buffer.indexOf("\n")) !== -1) {
        take(buffer.slice(0, cut));
        buffer = buffer.slice(cut + 1);
      }
    },
    // A stream that ends without a trailing newline still carries an event.
    flush() {
      const last = buffer;
      buffer = "";
      take(last);
    },
  };
}

/** An error the user has to see. Retrying it on another route changes nothing. */
export class CouncilRefused extends Error {
  constructor(message, { reason = null, status = null, detail = null } = {}) {
    super(message);
    this.name = "CouncilRefused";
    this.reason = reason;
    this.status = status;
    this.detail = detail;
  }
}

/** Turns a FastAPI error body into one sentence plus the reason behind it. */
export function refusalFrom(status, body) {
  const detail = body && typeof body === "object" ? body.detail : undefined;
  if (detail && typeof detail === "object") {
    return new CouncilRefused(detail.message || "The council was refused.", {
      reason: detail.reason || null,
      status,
      detail,
    });
  }
  return new CouncilRefused(
    typeof detail === "string" && detail ? detail : "The council failed.",
    { status, detail: detail ?? null }
  );
}

// 402 plan, 403 routing policy or spend cap, 422 bad request. All of these are
// the server's answer, not a hiccup, so the fallback would only ask twice.
const FINAL_STATUSES = [402, 403, 422];

function textOf(value, decoder) {
  // Real fetch hands over bytes. A string is what a test double, or a stream
  // polyfill without TextDecoder, hands over instead.
  if (typeof value === "string") return value;
  return decoder ? decoder.decode(value, { stream: true }) : String(value);
}

/**
 * Reads one council stream to its end. Calls onMember for every member as it
 * answers and returns the verdict event, which has the same shape as the
 * blocking route's response body.
 */
export async function readCouncilStream(response, { onMember } = {}) {
  const reader = response.body.getReader();
  const decoder = typeof TextDecoder === "function" ? new TextDecoder() : null;

  let verdict = null;
  let failure = null;

  const parser = createSseParser((event) => {
    if (event.type === "member") {
      if (onMember) onMember(event);
    } else if (event.type === "verdict") {
      verdict = event;
    } else if (event.type === "error") {
      failure = event;
    }
  });

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.push(textOf(value, decoder));
  }
  parser.flush();

  // The council ran and reported why it could not finish. That is an answer.
  if (failure) {
    throw new CouncilRefused(failure.message || "The council failed.", {
      reason: failure.reason || null,
    });
  }
  if (!verdict) {
    throw new Error("The council stream ended without a verdict.");
  }
  return verdict;
}

async function bodyOf(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/** The blocking route, unchanged in behaviour. Used on its own as the fallback. */
export async function runCouncilBlocking({ api, headers, body, fetchImpl }) {
  const response = await fetchImpl(`${api}/council`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw refusalFrom(response.status, await bodyOf(response));
  }
  return await response.json();
}

/**
 * Runs a council and reports progress. Streams when it can, falls back to the
 * blocking route when the stream cannot be established, and never falls back
 * after a member has already been reported, because that would run the whole
 * council a second time.
 */
export async function runCouncil({ api, headers, body, fetchImpl, onMember }) {
  const doFetch = fetchImpl || fetch;
  let started = false;

  try {
    const response = await doFetch(`${api}/council/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (FINAL_STATUSES.includes(response.status)) {
        throw refusalFrom(response.status, await bodyOf(response));
      }
      throw new Error(`Council stream unavailable (${response.status})`);
    }
    if (!response.body || typeof response.body.getReader !== "function") {
      throw new Error("Council stream carried no body");
    }

    return await readCouncilStream(response, {
      onMember: (event) => {
        started = true;
        if (onMember) onMember(event);
      },
    });
  } catch (error) {
    if (error instanceof CouncilRefused) throw error;
    if (started) throw error;
    // Nothing was shown to the user yet, so the blocking route can still
    // answer this message.
    return await runCouncilBlocking({ api, headers, body, fetchImpl: doFetch });
  }
}
